/**
 * ProjectLoader takes care of loading project incrementally
 * Interface to app:
 *      scheduleProjectLoad(pid)    to start loading a selected project (previous load may be ongoing)
 *      getActiveProjectId()        to get pid of the active in-use project
 */

import {
    ATTR_AWS_PROJECT_ID,
    ATTR_SORT_ORDER_I,
    DEBUG_MODE_TO_SAVE_SERVER_MSG,
    currentTimeMs,
    deactivateActiveProject,
    dumpObjToJsonFile,
    getActiveProjectID,
    getDevId,
    getUserEmail,
    makeAwsReqParams,
    prosLog, prosLogX,
    //prosLogDtX,
    prosLogDt,
    prosLogErr,
    reserveAwsApi,
    getActiveProject,
    restoreSelectedReports,
    prosLogWrn,
    updateShowingIdentifiers,
    checkAwsApiException, userHasDebugMenu,
} from "./ProsConnect";
import {API} from "aws-amplify";
import {viewNeedsCompleteProject} from "../containers/Home";

/**
 * View may subscribe to get notified when active project is changing to get events
 * Ref SUB_PROJ_LOAD_EV_ below are the events subscribe will receive
 * @param cb        Callback to be called - use arrow function for a closure context
 * @param name      Unique name per UI component listening on project change events
 */
var uiWantProjectLoadNotif = [];
export const SUB_PROJ_LOAD_EV_STARTED = "started"       // changed and started to load a new on
export const SUB_PROJ_LOAD_EV_INFO = "info"             // project settings, permissions and features known
export const SUB_PROJ_LOAD_EV_PROGRESS = "loading"      // Incremental load 1..N
export const SUB_PROJ_LOAD_EV_COMPLETED = "completed"   // when sub project load has been completed
export function subscribe_notifyProjectLoad(name, cb) {
    let subscriber = uiWantProjectLoadNotif.find(subs =>  subs.name === name)
    if (!subscriber) {
        uiWantProjectLoadNotif.push({
            cb: cb,
            name: name
        });
    } else {
        // even not desirable no harm to subscribe several times same client - update callback
        subscriber.cb = cb
    }
}

export function unsubscribe_notifyProjectLoad(name) {
    let uiIndex = uiWantProjectLoadNotif.findIndex(subs => subs.name === name)
    if (uiIndex >= 0) {
        uiWantProjectLoadNotif.splice(uiIndex,1);
    } else {
        prosLogWrn("unsubscribe_notifyProjectLoad w/o subscription:", name);
    }
}


let lastProjectSettingsId = undefined

/**
 * To forward project load notification event to subscribed view
 * @param ev    Ref SUB_PROJ_LOAD_EV_*
 */
export function notifyProjectLoadSubscribers(ev) {
    // send INFO event if is a one-step load and did not get INFO event explicitly
    // maintain also "last loaded" to repeat INFO when reloading same project again
    switch (ev) {
        case SUB_PROJ_LOAD_EV_STARTED: lastProjectSettingsId = undefined; break;
        case SUB_PROJ_LOAD_EV_PROGRESS:
        case SUB_PROJ_LOAD_EV_COMPLETED:
            if (lastProjectSettingsId !== getActiveProjectID()) {
                lastProjectSettingsId = getActiveProjectID()
                // After 1st incremental part know user permission and sub project parameters and features
                for (let subscriber of uiWantProjectLoadNotif) {
                    subscriber.cb(SUB_PROJ_LOAD_EV_INFO)
                }
            }
            break;
        default: break;
    }
    // all events are dispatched to subscribers
    for (let subscriber of uiWantProjectLoadNotif) {
        subscriber.cb(ev)
    }
}

// reports+search and first set of items starting newest first
const MAX_NUM_OF_INCREMENTAL_STEPS = 40
let incrementalContinue = undefined     // LastEvaluatedKey from where to continue next cycle
let incrementalSteps = 0                // Number of requests
let incrementalWaitMs = 0               // Cumulative waiting server
let incrementalProcessingTimeMs = 0     // Cumulative processing by web-UI
let incrementalStartTime = 0            // Time started
let projectLoadOngoing = false          // set true when project load started and false when complete project been loaded
                                        // Used to avoid refresh while loading incrementally


function reportProjectLoadStats() {
    // consider to save to server to collect performance statistics
    const logger = userHasDebugMenu() ? prosLogX : prosLog
    // show loading performance stats in console for super users
    let activeProject = getActiveProject()
    logger("Project load steps:",
        incrementalSteps,
        "wait:", incrementalWaitMs,
        "process:", incrementalProcessingTimeMs,
        "total:", currentTimeMs() - incrementalStartTime,
        "objCount:", activeProject ? activeProject.objs.length: '?')
    // If would report session error automatically after a project been loaded
    // reportSessionErrorToServer()
}

let incrementalTimer
function cancelIncrementalTimer() {
    if (incrementalTimer) {
        clearTimeout(incrementalTimer)
        incrementalTimer = undefined
    }
}

// Use timer to trigger incremental load steps to fetch project items
// when not all received in first query
function startIncrementalTimer(activeProject, firstTime) {
    cancelIncrementalTimer()
    incrementalSteps = firstTime ? 1 : incrementalSteps+1
    if (incrementalSteps > MAX_NUM_OF_INCREMENTAL_STEPS) {
        prosLogErr("Increment query exceeded")
        projectLoadOngoing = false
        return
    }
    incrementalTimer = setTimeout(async () => {
        if (incrementalContinue && activeProject &&
            incrementalContinue[ATTR_AWS_PROJECT_ID] === activeProject.getPid() ) {
            await awaitLoadProjectObjectsContinue()
        } else {
            console.log("Incremental load interrupted by project change")
        }
    }, 100);
}


let loadingCompleteProject = false
/**
 * This is a workaround for current subproject settings / location picture list which requires that
 * complete project been loaded which is an exception for all subproject settings.
 * This would not be needed after location picture editing has been changed as planned.
 * return true when started to load project data
 */
export function loadCompleteProjectIfNeeded() {
    if (!loadingCompleteProject) {
        awaitLoadProjectObjects(getActiveProjectID(), "loadCompleteProjectIfNeeded", false, true)
        return true
    }
    return false
}

/**
 * Load sub project incrementally max ~one second per transaction in high speed network
 * @param pid   Project id
 * @param who   Id of the caller for debugging
 * @param omitNotifications     True to bypass sending load notification for subscribers - used by "AcceptTerms"
 * @param forceComplete         True when explicitly requested to load a complete project.
 *                              This parameter is a WA for location picture list that should be removed later
 * @returns {Promise<*>}        True when 1st set of data been received - smaller project will have all items
 */
export async function awaitLoadProjectObjects(pid, who, omitNotifications=false, forceComplete) {
    prosLog("ProsConnect: awaitLoadProjectObjects() who:", who,
        "omitNotifications:", omitNotifications, "forceComplete:", forceComplete)
    if (!pid) {
        prosLogWrn("awaitLoadProjectObjects() nop pid - browser started")
        return false
    }
    projectLoadOngoing = true
    let activeProject = getActiveProject()
    incrementalStartTime = refreshUserIsActive()
    cancelIncrementalTimer()
    if (activeProject) {
        activeProject.clearUiToLoadNewProject();
    } else {
        prosLog("awaitLoadProjectObjects when no active project - create it")
        activeProject = getActiveProject(true)
    }
    if (!omitNotifications) {
        notifyProjectLoadSubscribers(SUB_PROJ_LOAD_EV_STARTED)
    }
    try {
        await reserveAwsApi(true, 'GET project objs ' + who)
        loadingCompleteProject = forceComplete || viewNeedsCompleteProject()
        const startedToFetch = currentTimeMs()
        let projectData = await API.get("prodect",
            '/websynch?email=' + getUserEmail()
            + '&id=' + getDevId()
            + '&oper=' + (loadingCompleteProject ? "get_project_incremental_start" : "get_project_info")
            + '&project=' + pid);
        incrementalWaitMs = currentTimeMs() - startedToFetch
        await reserveAwsApi(false, 'GET project objs ' + who)
        incrementalContinue = undefined
        incrementalSteps = 1
        if (Array.isArray(projectData.objs)) {
            projectData.objs.sort(function(a, b){return a[ATTR_SORT_ORDER_I]-b[ATTR_SORT_ORDER_I]});
            if (DEBUG_MODE_TO_SAVE_SERVER_MSG) {
                dumpObjToJsonFile(projectData, 'esim-yksi-projekti');
            }
            const processingStart = currentTimeMs()
            const loadedCompleteProject = projectData.continue === 'Done'
            activeProject.projectObjectsLoaded(projectData, loadedCompleteProject, incrementalWaitMs)
            if (loadingCompleteProject) {
                let selectedReportCount = restoreSelectedReports()
                // Feature #8.14 if no reports selected - select them all
                if (selectedReportCount < 1) {
                    activeProject.selectAllReportsIfNoneSelected()
                }
            }
            incrementalProcessingTimeMs = currentTimeMs() - processingStart
            if (!omitNotifications) {
                notifyProjectLoadSubscribers(loadingCompleteProject ?
                    SUB_PROJ_LOAD_EV_COMPLETED : SUB_PROJ_LOAD_EV_INFO
                )
            }
            if (loadedCompleteProject) {
                reportProjectLoadStats()
                projectLoadOngoing = false
            } else  {
                incrementalContinue = projectData.continue
                startIncrementalTimer(activeProject, true)
            }
            prosLogDt('awaitLoadProjectObjects() projects objects loaded', projectData.objs.length);
        } else {
            prosLogErr('awaitLoadProjectObjects() rx not an array', projectData);
            deactivateActiveProject()
        }
    } catch (e) {
        prosLogErr(e);
        deactivateActiveProject()
    }
    await reserveAwsApi(false, 'GET project objs ' + who)
    prosLogDt("ProsConnect: awaitLoadProjectObjects() - done - started by:", who)
    return activeProject && activeProject.isProjectLoaded()
}

// May be repeated several times to get rest of the project object when not all received in first data set
export async function awaitLoadProjectObjectsContinue() {
    cancelIncrementalTimer()
    let activeProject = getActiveProject()
    let req = makeAwsReqParams(true)
    req.oper = 'get_project_incremental_cont'
    req.project = incrementalContinue[ATTR_AWS_PROJECT_ID]
    req.continue = incrementalContinue
    req.id = req.device_id
    prosLogDt('awaitLoadProjectObjectsContinue() projects objects continue', req.project);
    try {

        await reserveAwsApi(true, 'POST continue objs')
        const startedToFetch = currentTimeMs()
        let contData = await API.post("prodect",'/webstore',{body: req})
        const fetchTime = currentTimeMs() - startedToFetch
        incrementalWaitMs += fetchTime
        await reserveAwsApi(false, 'POST continue objs')

        incrementalContinue = undefined
        if (Array.isArray(contData.objs)) {
            contData.objs.sort(function(a, b){return a[ATTR_SORT_ORDER_I]-b[ATTR_SORT_ORDER_I]});
            if (DEBUG_MODE_TO_SAVE_SERVER_MSG) {
                dumpObjToJsonFile(contData, 'esim-yksi-projekti-' + incrementalSteps);
            }
            prosLog('awaitLoadProjectObjects() projects objects loaded:',
                contData.objs.length, 'step:', incrementalSteps, 'wait:', fetchTime);
            let processingStart = currentTimeMs()
            const loadedCompleteProject = contData.continue === 'Done'
            if (loadedCompleteProject) {
                incrementalContinue = undefined
                projectLoadOngoing = false
                notifyProjectLoadSubscribers(SUB_PROJ_LOAD_EV_COMPLETED)
                reportProjectLoadStats()
            } else {
                incrementalContinue = contData.continue
                notifyProjectLoadSubscribers(SUB_PROJ_LOAD_EV_PROGRESS)
                startIncrementalTimer(activeProject, false)
            }
            if (activeProject) {
                // console.log('refresh objs:', contData.objs.length)
                activeProject.handleRefresh(contData.objs)
                incrementalProcessingTimeMs += currentTimeMs() - processingStart
            }
        } else {
            prosLogErr('awaitLoadProjectObjectsContinue() rx not an array', contData);
            projectLoadOngoing = false
        }
    } catch (e) {
        checkAwsApiException(e)
        projectLoadOngoing = false
    }
    await reserveAwsApi(false, 'POST continue objs')
    return activeProject && activeProject.isProjectLoaded()
}

export function isLoadingProject() {
    return projectLoadOngoing
}

export function getProjectLoadStatusPercentage() {
    if (!projectLoadOngoing) {
        return 0
    }
    if (!incrementalContinue) {
        // Loading 1st set
        return 0
    }
    let activeProject = getActiveProject()
    // console.log("incrementalContinue.age:", incrementalContinue.age, "activeProject.getAge()", activeProject.getAge())
    let percentage = (Math.round(100 * incrementalContinue.age / activeProject.getAge()))
    return percentage ? percentage : 1
}

export function updateUiPollingActivity(pendingAdded) {
    let activeProject = getActiveProject()
    let numOfActivePolling = uiWantRefresh.filter(subs => subs.pollForChanges).length;
    let needToPoll = numOfActivePolling > 0 && (pendingAdded || activeProject?.hasPendingItems())
    if (needToPoll && !uiActivePolling) {
        pollingTimer(true);
        uiActivePolling = true;
    } else if (!needToPoll && uiActivePolling) {
        pollingTimer(false);
        uiActivePolling = false;
    }
}

export function subscribeRefreshUiWhenDataChanged(pollForChanges, name, cb) {
    let subscriber = uiWantRefresh.find(subs =>  subs.name === name);
    if (!subscriber) {
        uiWantRefresh.push({
            cb: cb,
            pollForChanges: pollForChanges,
            name: name
        });
    } else {
        subscriber.pollForChanges = pollForChanges;
        subscriber.cb = cb
    }
    let project = getActiveProject()
    if (project && project.hasPendingItems()) {
        updateUiPollingActivity();
    }
}

// This polling below is to poll for changes in project (age change) and autonomously
// and to trigger loading changes and notify views by refreshui.
// This is used by picture handling to wait for making a compressed picture,
// rotate and PDF to image conversion. Such a use-case could be replaced by "longQuery" for faster response.
// TODO - change picture operations to use long query to remove automatic polling
const POLL_INTERVALL_MS = 7000;     // Periodicity to poll picture operations
var synchPollTimer = null;
var timeUserLastActiveMs = 0;
let uiWantRefresh = [];
let uiActivePolling=false;
export async function pollChanges(forcePoll) {
    let activeProject = getActiveProject()
    if (!synchPollTimer || !uiWantRefresh.length || !activeProject || !uiActivePolling) {
        return;
    }
    if (forcePoll || activeProject.hasPendingItems()) {
        // prosLogX('pollChanges() forcePoll:', forcePoll);
        await activeProject.awaitRefreshProjectObjectsFromAws();
    } else {
        updateUiPollingActivity()
    }
}

export function refreshUserIsActive() {
    timeUserLastActiveMs = currentTimeMs()
    return timeUserLastActiveMs
}

function pollingTimer(start) {
    if (synchPollTimer) {
        clearInterval(synchPollTimer);
        synchPollTimer = null;
    }
    if (start) {
        synchPollTimer = setInterval(()=> pollChanges(), POLL_INTERVALL_MS);
    }
}

export function unSubscribeRefreshUiWhenDataChanged(name) {
    let uiIndex = uiWantRefresh.findIndex(subs => subs.name === name);
    if (uiIndex >= 0) {
        uiWantRefresh.splice(uiIndex,1);
        // prosLogX("--unSubscribeRefreshUiWhenDataChanged:", name);
    } else {
        prosLogErr("--unSubscribeRefreshUiWhenDataChanged but no UI subscribed:", name);
    }
    updateUiPollingActivity();
}

/**
 * Inform subscriber about update
 * @param who     Name of functionality that informs about refresh
 * @param change  Information about change that may be used by client
 *                  undefined - default - no specific reason
 *                  {newObsId: <uuid>}  - new OBS been added to be opened in table and calendar popup
 */
export function refreshUi(who, change) {
    updateShowingIdentifiers()
    for (let subscriber of uiWantRefresh) {
        subscriber.cb(who, change);
    }
}

export function deferredRefreshUi() {
    setTimeout(() => refreshUi(), 200)
}
